import { match } from 'ts-pattern';
import { z } from 'zod';
import {
  orderContext,
  type Order,
} from '~/app/features/order-middleware/orderMiddleware';
import { shippingProtectionClaimStateSchema } from '~/stores/slices/shippingProtectionClaim';
import api from '~/utils/api';
import { GspReasonLabels } from '~/utils/constants';
import { getDeviceFingerprint } from '~/utils/fingerprint';
import { addBreadcrumb } from '~/utils/observability';
import { makeAction, makeLoader, redirect } from '~/utils/routing';
import { shippingClaimContext } from '../shippingClaimMiddleware';

const completeShippingClaim = z.object({
  reason: shippingProtectionClaimStateSchema.unwrap().shape.reason.unwrap(),
  itemIds: shippingProtectionClaimStateSchema.unwrap().shape.itemIds.min(1),
  meta: shippingProtectionClaimStateSchema.unwrap().shape.meta.unwrap(),
  customerInfo: shippingProtectionClaimStateSchema
    .unwrap()
    .shape.customerInfo.unwrap(),
});

export const { loader, useLoaderData } = makeLoader(({ context }) => {
  const order = context.get(orderContext);
  const shippingProtectionClaim = context.get(shippingClaimContext);

  return match(completeShippingClaim.safeParse(shippingProtectionClaim))
    .with({ success: true }, ({ data: completedClaim }) => ({
      customerInfo: completedClaim.customerInfo,
      items: order.lineItems.filter((item) =>
        completedClaim.itemIds.includes(item.id),
      ),
      reason: GspReasonLabels[completedClaim.reason],
      meta: completedClaim.meta,
    }))
    .with({ success: false }, ({ error }) => {
      addBreadcrumb({
        message:
          'Invalid shipping protection claim context at shipping claim review loader',
        level: 'warning',
        data: { error },
      });
      return redirect(`../${order.idFromPlatform}`);
    })
    .exhaustive();
});

const createProtectionClaim = async (
  order: Order,
  data: z.infer<typeof completeShippingClaim>,
) => {
  const fingerprint = await getDeviceFingerprint(order.email);

  return api.createProtectionClaim({
    params: { storefrontId: order.storefrontId },
    body: {
      reason: data.reason,
      originalStoreOrder: {
        id: order.id,
        lineItems: order.lineItems
          .filter((item) => data.itemIds.includes(item.id))
          .map(({ id, quantity }) => ({
            id,
            quantity,
          })),
      },
      noteFromCustomer: data.meta.noteFromCustomer,
      images: data.meta.assets?.map((asset) => asset.cdnName) ?? [],
      fingerprint,
      shippingAddress: {
        ...data.customerInfo.address,
        firstName: data.customerInfo.firstName,
        lastName: data.customerInfo.lastName,
      },
    },
  });
};

export const { action, useActionResult: useReviewActionResult } = makeAction(
  async ({ context }, appStore) => {
    const order = context.get(orderContext);
    const shippingProtectionClaim = context.get(shippingClaimContext);

    return match(completeShippingClaim.safeParse(shippingProtectionClaim))
      .with({ success: true }, async ({ data }) => {
        const claim = await createProtectionClaim(order, data);
        appStore.dispatch(appStore.actions.completeShippingProtectionClaim());

        return redirect(`../../claim/${claim.id}/new`);
      })
      .with({ success: false }, ({ error }) => {
        throw new Error('Invalid shipping claim action context', {
          cause: error,
        });
      })
      .exhaustive();
  },
);
